<template>
  <Dialog
    :open="modelValue"
    as="div"
    class="relative z-50"
    @close="emit('update:modelValue', false)"
  >
    <div
      class="fixed inset-0 bg-black/30"
      aria-hidden="true"
    />
    <div class="fixed inset-0 flex items-center justify-center p-2 text-gray-700">
      <DialogPanel class="flex w-full max-w-md flex-col rounded bg-gray-50 p-3 md:p-5">
        <button
          class="mb-2 ml-auto focus:outline-none"
          @click="emit('update:modelValue', false)"
        >
          <XMarkIcon class="size-5 md:size-6" />
        </button>
        <DialogTitle class="w-full text-center font-serif text-2xl md:text-3xl">
          Suscripción {{ planName }}
        </DialogTitle>
        <DialogDescription class="mt-2 text-center text-sm text-gray-700 md:mt-3 md:text-base">
          Agrega todos los detalles adicionales que te gustaría tener en una suscripción.
        </DialogDescription>
        <form
          class="mt-3 flex flex-col"
          @submit.prevent="onSubmit"
        >
          <p class="text-sm text-gray-700 md:text-base">Nombre de plan:</p>
          <p class="rounded-md border border-lk-light-gray bg-white p-2 text-xs text-gray-700 md:p-2.5 md:text-sm">
            {{ planName }}
          </p>
          <p class="mt-3 text-sm text-gray-700 md:text-base">Monto de la suscripción:</p>
          <p class="rounded-md border border-lk-light-gray bg-white p-2 text-xs text-gray-700 md:p-2.5 md:text-sm">
            {{ useFormatCurrency(form.amount) }}
          </p>
          <div class="mt-3 flex flex-col">
            <label class="mb-1 text-sm text-gray-700 md:text-base">Características adicionales</label>
            <textarea
              v-model="form.comment"
              class="rounded-md border border-lk-light-gray p-2 text-xs focus:border-lk-green focus:outline-none md:text-sm"
              placeholder="Cuéntanos qué características adicionales te gustaría tener en una suscripción..."
              rows="4"
            />
            <p class="mt-1 text-xs text-gray-700 md:text-sm">
              Nota: esto es una intención de suscripción, no una suscripción real aún.
            </p>
          </div>
          <lokal-loading
            v-if="isLoading"
            :loading="isLoading"
            class="self-center"
          />
          <div
            v-else
            class="mt-5 flex w-full flex-col items-center space-y-2"
          >
            <button
              type="submit"
              class="w-full rounded-md bg-lk-green p-2 text-sm text-white transition duration-200 hover:bg-lk-green-deep md:text-base"
            >
              Enviar Solicitud
            </button>
            <button
              type="button"
              class="text-xs text-gray-600 hover:underline md:text-sm"
              @click="emit('update:modelValue', false)"
            >
              Cancelar
            </button>
          </div>
        </form>
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useForm } from 'vee-validate';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { Dialog, DialogPanel, DialogTitle, DialogDescription } from '@headlessui/vue';
import { useMutation } from 'vue-query';
import subscriptionIntentionsApi from 'api/subscription-intentions';
import useSessionStore from '../stores/user-store';
import LokalLoading from './shared/lokal-loading.vue';
import useFormatCurrency from './use/format-currency';
import useMessageStore from 'stores/message-store';

interface SubscriptionDetailsDialogProps {
  modelValue: boolean;
  selectedPlan: 'basic' | 'plus' | 'premium';
  amount: number;
}

const props = withDefaults(defineProps<SubscriptionDetailsDialogProps>(), {
  modelValue: false,
  selectedPlan: 'basic',
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const sessionStore = useSessionStore();
const messageStore = useMessageStore();

const form = ref({
  name: `${sessionStore.user.firstName} ${sessionStore.user.lastName}`,
  email: sessionStore.user.email || '',
  amount: props.amount,
  comment: '',
});

const planName = computed(() => {
  if (props.selectedPlan === 'basic') return 'Basic';
  if (props.selectedPlan === 'plus') return 'Plus';
  if (props.selectedPlan === 'premium') return 'Premium';
  return '';
});

const createSubscriptionIntention = useMutation(
  () => {
    const data: Partial<SubscriptionIntention> = {
      subscriptionType: props.selectedPlan,
      amount: form.value.amount,
      comments: form.value.comment,
    };

    return subscriptionIntentionsApi.create(data);
  },
  {
    onSuccess: () => {
      emit('update:modelValue', false);
      messageStore.showMessage('Intención de suscripción creada correctamente', 'success');
    },
  },
);
const isLoading = computed(() => createSubscriptionIntention.isLoading.value);

const { handleSubmit } = useForm();
const onSubmit = handleSubmit(async () => {
  await createSubscriptionIntention.mutateAsync();
});
</script>
